/* ELIO shipping meter controller.
   Subscribes to elio-cart-state.js. Owns no cart logic of its own.

   Also owns the estimated-shipping strikethrough, because the struck/unstruck
   decision is the same qualification decision and must never disagree with the
   meter sitting inches above it.

   No dollar amount is displayed anywhere. elio-ship-strip.js still geo-resolves
   a DELIVERY TIME line, which is honest and useful, but its cost table now has
   no render target and writes nothing.
*/
(function () {
  'use strict';

  var T = window.ElioT || function (k, fallback) { return fallback; };

  function copy(key, fallback) {
    var node = document.querySelector('[data-elio-shipcopy="' + key + '"]');
    if (node) return node.textContent.trim();
    return fallback;
  }

  var STR = {
    threshold:    function () { return copy('threshold',     'Free U.S. shipping on orders over $25.'); },
    // NOTE: the $25 above is only a last-ditch fallback if the copy bridge is
    // missing. In practice the bridge always renders from settings.freeship_threshold.
    // Bridge key is 'included' for backwards compatibility with the markup
    // already deployed. The VALUE it holds is the unlocked string.
    unlocked:     function () { return copy('included',      "You've unlocked free U.S. shipping."); },
    withPair:     function () { return copy('with_pair',     'Free U.S. shipping with this pair.'); },
    toGo:         function () { return copy('to_go',         '[amount] to go'); },
    // Under-threshold copy addresses the shopper, mirroring the success state.
    // "Free U.S. shipping on orders over $25." is a policy notice; it states a
    // rule and leaves the shopper to do the arithmetic. This states their
    // position and the distance, which is the part that actually moves.
    away:         function () { return copy('away',          "You're [amount] away from free U.S. shipping."); },
    free:         function () { return copy('free',          'Free'); }
  };

  function money(cents) {
    var v = Number(cents || 0) / 100;
    // Whole dollars read cleaner in a nudge: "$5 to go", not "$5.00 to go".
    if (Math.abs(v - Math.round(v)) < 0.005) return '$' + Math.round(v);
    return '$' + v.toFixed(2);
  }

  /* ---- Estimated shipping strikethrough --------------------------------- */

  /* The shipping VALUE, not an estimate.

     There is no dollar figure any more. A pre-address estimate cannot match what
     Shopify charges at checkout, and now that sub-threshold carts actually pay,
     a wrong number is a broken promise rather than a cosmetic detail.

     Two honest states only:
       qualified      -> "Free"                    (we know this for certain)
       everything else-> "Calculated at checkout"  (including unknown/pending)

     Strings come from data attributes so they stay authorable in Liquid. */
  function paintShipping(qualifies, known) {
    var nodes = document.querySelectorAll('[data-elio-shipvalue]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var free = n.getAttribute('data-ship-free') || 'Free';
      var calc = n.getAttribute('data-ship-calc') || 'Calculated at checkout';
      var isFree = !!(known && qualifies);
      n.textContent = isFree ? free : calc;
      n.classList.toggle('is-free', isFree);
    }
  }

  /* ---- Render ----------------------------------------------------------- */

  var lastAnnounced = '';

  // Writes a template like "You're [amount] away from X." into a node, with the
  // amount wrapped for emphasis. Built as text nodes, so nothing in the template
  // can inject markup.
  function writeTemplated(node, template, amount) {
    node.textContent = '';
    var parts = String(template).split('[amount]');
    node.appendChild(document.createTextNode(parts[0] || ''));
    if (parts.length > 1) {
      var em = document.createElement('span');
      em.className = 'elio-shipmeter__amt';
      em.style.fontWeight = '600';
      em.style.fontVariantNumeric = 'tabular-nums';
      em.textContent = amount;
      node.appendChild(em);
      node.appendChild(document.createTextNode(parts.slice(1).join('[amount]')));
    }
  }

  function render(detail) {
    var meters = document.querySelectorAll('[data-elio-shipmeter]');
    if (!meters.length) return;

    if (!detail.marketIsUS) {
      meters.forEach(function (m) { m.style.display = 'none'; });
      return;
    }

    meters.forEach(function (meter) {
      /* Every meter reports the same thing: the actual cart. The PDP used to
         project the selected option instead, which meant it said different words
         than the drawer for the same cart and could claim a threshold was met
         before anything was added. One source, one sentence, everywhere. */
      var view = {
        ready: detail.ready,
        qualifies: detail.qualifies,
        qualifyingCents: detail.qualifyingCents,
        remainingCents: detail.remainingCents,
        thresholdCents: detail.thresholdCents
      };

      // Threshold unreadable (setting deleted/zero): the Liquid fallback line is
      // already correct and there is no distance to report. Do not overwrite it.
      if (!(view.thresholdCents > 0)) {
        meter.classList.remove('is-pending');
        return;
      }

      meter.classList.toggle('is-pending', !view.ready);
      meter.classList.toggle('is-qualified', !!(view.ready && view.qualifies));

      var primary = meter.querySelector('[data-shipmeter-primary]');
      var status  = meter.querySelector('[data-shipmeter-status]');
      var track   = meter.querySelector('[data-shipmeter-track]');
      var fill    = meter.querySelector('[data-shipmeter-fill]');
      var live    = meter.querySelector('[data-shipmeter-live]');

      if (!view.ready) {
        // Neutral. State the offer, claim nothing about this cart.
        if (primary) primary.textContent = STR.threshold();
        if (status) status.textContent = '';
        if (fill) fill.style.width = '0%';
        if (track) track.setAttribute('aria-valuenow', '0');
        return;
      }

      var pct = Math.max(0, Math.min(100,
        (view.qualifyingCents / view.thresholdCents) * 100));
      if (fill) fill.style.width = pct.toFixed(2) + '%';

      if (track) {
        track.setAttribute('aria-valuemax', (view.thresholdCents / 100).toFixed(2));
        track.setAttribute(
          'aria-valuenow',
          (Math.min(view.qualifyingCents, view.thresholdCents) / 100).toFixed(2)
        );
      }

      var primaryText, statusText;

      if (view.qualifies) {
        primaryText = STR.unlocked();
        statusText = '';
      } else {
        primaryText = null;   // written below, with the amount emphasised
        statusText = '';      // the figure now lives inside the sentence
      }

      if (primary) {
        if (primaryText === null) {
          writeTemplated(primary, STR.away(), money(view.remainingCents));
          primaryText = primary.textContent;
        } else {
          primary.textContent = primaryText;
        }
      }
      if (status) status.textContent = statusText;

      // Announce meaningful changes only, once, and not per animation frame.
      if (live) {
        var announce = view.qualifies ? primaryText : (primaryText + ' ' + statusText);
        if (announce !== lastAnnounced) {
          lastAnnounced = announce;
          live.textContent = announce;
        }
      }
    });

    // Follows the ACTUAL cart, never the PDP projection: a projected
    // qualification is not a qualification at checkout. While state is unknown
    // (mid cart-update) it reads "Calculated at checkout", never "Free".
    paintShipping(detail.qualifies, detail.ready && detail.marketIsUS);
  }


  /* ---- Crossing moment ---------------------------------------------------
     The meter is passive: it only speaks where it is rendered. Crossing the
     threshold is an event, and the shopper is usually looking at a product,
     not the cart. This fires once, on the actual cart going from not-qualified
     to qualified, anywhere on the site.

     Built entirely in JS. No Liquid render, no snippet, no markup dependency,
     so it cannot throw a storefront error. If the CSS asset is missing it
     still announces to screen readers, it just looks plain.
  ------------------------------------------------------------------------ */

  var TOAST_ID = 'elioShipToast';
  var toastTimer = null;

  function buildToast() {
    var el = document.getElementById(TOAST_ID);
    if (el) return el;
    el = document.createElement('div');
    el.id = TOAST_ID;
    el.className = 'elio-shiptoast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML =
      '<span class="elio-shiptoast__rule" aria-hidden="true"></span>' +
      '<span class="elio-shiptoast__text"></span>' +
      '<button type="button" class="elio-shiptoast__x" aria-label="Dismiss">&times;</button>';
    el.querySelector('.elio-shiptoast__x').addEventListener('click', hideToast);
    document.body.appendChild(el);
    return el;
  }

  function hideToast() {
    var el = document.getElementById(TOAST_ID);
    if (!el) return;
    el.classList.remove('is-in');
    clearTimeout(toastTimer);
  }

  function showToast(text) {
    var el = buildToast();
    el.querySelector('.elio-shiptoast__text').textContent = text;
    // force reflow so the transition runs even on repeat shows
    void el.offsetWidth;
    el.classList.add('is-in');
    clearTimeout(toastTimer);
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    toastTimer = setTimeout(hideToast, reduced ? 6000 : 4500);
  }

  var prevQualified = null;   // null until the first real read
  function watchCrossing(detail) {
    if (!detail.ready || !detail.marketIsUS) return;
    var now = !!detail.qualifies;

    // First read of the session establishes the baseline. A cart that already
    // qualified on arrival did not just cross, so it gets no toast.
    if (prevQualified === null) { prevQualified = now; return; }

    if (now && !prevQualified) {
      showToast(STR.unlocked());
    }
    // Falling back under the threshold silently re-arms it. No toast for losing
    // the offer: that would be nagging, and the meter already shows the change.
    prevQualified = now;
  }

  /* The theme re-renders the cart drawer and cart page on every mutation, which
     replaces the meter element with its Liquid initial markup. Repaint the
     instant that happens, from the state we already hold, so the freshly
     rendered default never lingers on screen. */
  var repaintQueued = false;
  function repaintSoon() {
    if (repaintQueued) return;
    repaintQueued = true;
    requestAnimationFrame(function () {
      repaintQueued = false;
      if (window.ElioShipState) render(window.ElioShipState.get());
    });
  }

  function watchForRerender() {
    if (!window.MutationObserver) return;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && (n.matches('[data-elio-shipmeter]') || n.querySelector('[data-elio-shipmeter]'))) {
            repaintSoon();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    if (!window.ElioShipState) return;
    window.ElioShipState.subscribe(render);
    window.ElioShipState.subscribe(watchCrossing);
    watchForRerender();

    // Cart mutations: repaint from known state immediately, then again once the
    // authoritative read lands (the state module re-reads on the same events).
    ['cart:update','cart:updated','cart:change','cart:refresh'].forEach(function (e) {
      document.addEventListener(e, repaintSoon);
    });

    // PDP: re-render when the purchase configuration changes.
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.elio-pdp__pair-card')) {
        setTimeout(function () { render(window.ElioShipState.get()); }, 30);
      }
    });
    document.addEventListener('change', function (e) {
      if (e.target.closest && e.target.closest('.elio-pdp__pair, .elio-pdp__qty, [data-elio-qty]')) {
        setTimeout(function () { render(window.ElioShipState.get()); }, 30);
      }
    });
    // Colour swap changes the price tier ($20 vs $24), so it changes projection.
    document.addEventListener('elio:pdp:swapped', function () {
      setTimeout(function () { render(window.ElioShipState.get()); }, 60);
    });
    // Cart drawer mounts late; repaint when it opens.
    document.addEventListener('cart:refresh', function () {
      setTimeout(function () { render(window.ElioShipState.get()); }, 60);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
